/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
/* eslint-disable max-len */
import React from 'react';
import { RobotCloudAPI, AppProxyAPI, FileRequestAction } from '@moderepo/mode-apis';
import { Agent, GatewayDefinition, Project, ProjectType } from '@moderepo/mode-internal-apis';
import { getSubRouteAbsolutePath, RouteGroup, SlideOutCompProps, SubRoute } from '@moderepo/mode-app-components';
import { SlideOutPanelOptions } from '@moderepo/mode-ui-state';
import { Route } from 'react-router-dom';
import { RitzProjectRoute, RitzProjectRouteProps } from '../routes/RitzProjectRoute';
import { RCMonitorsPage } from './RCMonitorsPage';
import { RCIssuesPage } from './RCIssuesPage';
import { RCInvestigateIssuePage } from './RCInvestigateIssuePage';
import { RCDeviceIssuesPage } from './RCDeviceIssuesPage';
import { RCHomeIssuesPage } from './RCHomeIssuesPage';
import { DEFAULT_ROUTE_PATHS } from '../routes/AppRoutes';
import { RCRobotDashboardPage } from './RCRobotDashboardPage';
import { RCRobotHistoricalDataPage } from './RCRobotHistoricalDataPage';
import { RCRobotsPage } from './RCRobotsPage';
import { RCHomeRobotsPage } from './RCHomeRobotsPage';
import { RCDeviceRobotsPage } from './RCDeviceRobotsPage';
import { DEFAULT_SUB_ROUTE_NAME, ProjectTypeAppProxyHandler, ProjectTypeHandlersManager, ProjectTypeRoutesHandler,
    ProjectTypeSlideOutCompHandler, ProjectTypeCustomUIHandler, ProjectTypePageLayoutHandler } from '../../utils/ritzExtensionUtils';
import { RCMonitorPreviewContainer } from './RCMonitorPreviewContainer';
import { RCAlertPreviewContainer } from './RCAlertPreviewContainer';
import { RCRobotPreviewContainer } from './RCRobotPreviewContainer';
import { RCRobotInfoPage } from './RCRobotInfoPage';
import { RCRobotIssuesPage } from './RCRobotIssuesPage';
import { RCLinkRobotPreviewContainer } from './RCLinkRobotPreviewContainer';
import { RCProjectDevicesPage } from './RCProjectDevicesPage';
import { RCHomeDetailsPage } from './RCHomeDetailsPage';
import { RCGatewayCatalogPage } from './admin/RCGatewayCatalogPage';
import { RCGatewayDefinitionPreviewContainer } from './admin/RCGatewayDefinitionPreviewContainer';
import { RCRobotCatalogPage } from './admin/RCRobotCatalogPage';
import { RCRobotDefinitionPreviewContainer } from './admin/RCRobotDefinitionPreviewContainer';
import { RCHomeRobotsStatusPage } from './RCHomeRobotsStatusPage';
import { RCDeviceCollectionsLatestValuesDisplayPage } from './RCDeviceCollectionsLatestValuesDisplayPage';
import { RCProjectSettingsPage } from './RCProjectSettingsPage';
import { RCIssuePreviewContainer } from './RCIssuePreviewContainer';
import { GenericDataViewPage } from '../time-series/GenericDataViewPage';
import { getRCDefaultProjectUIConfig } from './RCProjectUIConfig';
import { integrateProjectTypeCustomUIConfigWithProjectCustomConfig, ProjectUIConfig } from '../../utils/ritzUIExtensionUtils';
import { RCFileRequestPreviewContainer, RCFileRequestsPage } from './file-transfer';
import { RCPageInfoBarContainer, RCPageInfoBarContainerProps } from './RCPageInfoBarContainer';
import { RCDeviceSettingsPage } from '.';


/**
 * Route names specific to robot cloud project
 */
export enum RC_SUB_ROUTE_NAME {
    ROBOTS = 'robots',
    ROBOTS_STATUS = 'robots_status',
    ISSUES = 'issues',
    FILE_REQUESTS = 'file_requests'
}



/**
 * These are the list of RobotCloud components that can be displayed in the SlideOut panel.
 */
export enum RCSlideOutPanelCompType {
    MONITOR_PREVIEW = 'monitor_preview',
    ALERT_PREVIEW = 'rc_alert_preview',
    ISSUE_PREVIEW = 'issue_preview',
    ROBOT_PREVIEW = 'robot_preview',
    LINK_ROBOT_SELECTOR = 'link_robot_selector',
    GATEWAY_DEFINITION_PREVIEW = 'gateway_def_preview',
    ROBOT_DEFINITION_PREVIEW = 'robot_def_preview',
    FILE_REQUEST_PREVIEW = 'file_request_preview',
}


/**
 * The options required for showing preview of a Monitor in the Slide Out panel
 */
export interface RCMonitorPreviewCompOptions extends SlideOutPanelOptions {
    readonly compType: RCSlideOutPanelCompType.MONITOR_PREVIEW;
    readonly compProps: {
        readonly data: {
            readonly projectId: number;
            readonly monitorId?: number | undefined;
            readonly preSelectedRobotClass?: string | undefined;
            readonly preSelectedRobotId?: string | undefined;
            readonly preSelectedHomeId?: number | undefined;
        }
        readonly context?: {
            readonly userId?: number | undefined;
            readonly homeId?: number | undefined;
            readonly deviceId?: number | undefined;
        } | undefined;
    }
}


/**
 * The options required for showing preview of an Alert in the Slide Out panel
 */
export interface RCAlertPreviewCompOptions extends SlideOutPanelOptions {
    readonly compType: RCSlideOutPanelCompType.ALERT_PREVIEW;
    readonly compProps: {
        readonly data: {
            readonly projectId: number;
            readonly alertId: number;
            readonly homeId: number;
            readonly robotId: string;
        };
        readonly context?: {
            readonly userId?: number;
            readonly homeId?: number;
        } | undefined;
    }
}


/**
 * The options required for showing preview of an Issue in the Slide Out panel
 */
export interface RCIssuePreviewCompOptions extends SlideOutPanelOptions {
    readonly compType: RCSlideOutPanelCompType.ISSUE_PREVIEW;
    readonly compProps: {
        readonly data: {
            readonly projectId: number;
            readonly issueId?: number;
            readonly preSelectedRobotId?: string;
            readonly preSelectedHomeId?: number;
        };
        readonly context?: {
            readonly userId?: number;
            readonly homeId?: number;
            readonly deviceId?: number;
        } | undefined;
    }
}


/**
 * The options required for showing preview of a Robot in the Slide Out panel
 */
export interface RCRobotPreviewCompOptions extends SlideOutPanelOptions {
    readonly compType: RCSlideOutPanelCompType.ROBOT_PREVIEW;
    readonly compProps: {
        readonly data: {
            projectId: number;
            readonly homeId: number;
            readonly robotId?: string;
        };
        readonly context?: {
            readonly userId?: number;
            readonly homeId?: number;
            readonly deviceId?: number;
        } | undefined;
    }
}


/**
 * The options required for showing the Link Robot Selector in the Slide Out panel
 */
export interface RCLinkRobotSelectorCompOptions extends SlideOutPanelOptions {
    readonly compType: RCSlideOutPanelCompType.LINK_ROBOT_SELECTOR;
    readonly compProps: {
        readonly data: {
            readonly projectId: number;
            readonly homeId: number;
            readonly deviceId: number;
        };
        readonly context?: {
            readonly userId?: number;
            readonly homeId?: number;
            readonly deviceId?: number;
        } | undefined;
    }
}


/**
 * The options required for showing preview of a Gateway Definition in the Slide Out panel
 */
export interface RCGatewayDefinitionPreviewCompOptions extends SlideOutPanelOptions {
    readonly compType: RCSlideOutPanelCompType.GATEWAY_DEFINITION_PREVIEW;
    readonly compProps: {
        readonly data: {
            readonly projectId: number;
            readonly deviceClass: string | undefined;
        }
    }
}


/**
 * The options required for showing preview of a Robot Definition in the Slide Out panel
 */
export interface RCRobotDefinitionPreviewCompOptions extends SlideOutPanelOptions {
    readonly compType: RCSlideOutPanelCompType.ROBOT_DEFINITION_PREVIEW;
    readonly compProps: {
        readonly data: {
            readonly projectId: number;
            readonly robotClass: string | undefined;
        }
    }
}


/**
 * The options required for showing preview of a File Request in the Slide Out panel
 */
export interface RCFileRequestPreviewCompOptions extends SlideOutPanelOptions {
    readonly compType: RCSlideOutPanelCompType.FILE_REQUEST_PREVIEW;
    readonly compProps: {
        readonly data: {
            readonly projectId: number;
            readonly homeId: number;
            readonly deviceId: number;
            readonly robotId?: string | undefined;

            // selectedFileRequest can be either a number or a FileRequestAction. When a file request is selected and we want to show that file
            // request details in the preview panel, we will set the fileRequest's ID to this selectedFileRequest field. When we need to create a new
            // file request, we will set a FileRequestAction to this field.
            readonly selectedFileRequest: number | FileRequestAction;
        };
        readonly context?: {
            readonly userId?: number | undefined;
            readonly homeId?: number | undefined;
            readonly deviceId?: number | undefined;
            readonly robotId?: string | undefined;
        } | undefined;
    }
}


/**
 * Type guard check to make sure the provided options is the options for RCMonitorPreviewCompOptions
 * @param options
 */
export const isMonitorPreviewCompOptions = (options: unknown): options is RCMonitorPreviewCompOptions => {
    return (options as SlideOutPanelOptions)?.compType === RCSlideOutPanelCompType.MONITOR_PREVIEW;
};


/**
 * Type guard check to make sure the provided options is the options for RCIssuePreviewCompOptions
 * @param options
 */
export const isIssuePreviewCompOptions = (options: unknown): options is RCIssuePreviewCompOptions => {
    return (options as SlideOutPanelOptions)?.compType === RCSlideOutPanelCompType.ISSUE_PREVIEW;
};


/**
 * Type guard check to make sure the provided options is the options for RCIssuePreviewCompOptions
 * @param options
 */
export const isAlertPreviewCompOptions = (options: unknown): options is RCAlertPreviewCompOptions => {
    return (options as SlideOutPanelOptions)?.compType === RCSlideOutPanelCompType.ALERT_PREVIEW;
};


/**
 * Type guard check to make sure the provided options is the options for RCRobotPreviewCompOptions
 * @param options
 */
export const isRobotPreviewCompOptions = (options: unknown): options is RCRobotPreviewCompOptions => {
    return (options as SlideOutPanelOptions)?.compType === RCSlideOutPanelCompType.ROBOT_PREVIEW;
};

/**
 * Type guard check to make sure the provided options is the options for RCLinkRobotSelectorCompOptions
 * @param options
 */
export const isLinkRobotSelectorCompOptions = (options: unknown): options is RCLinkRobotSelectorCompOptions => {
    return (options as SlideOutPanelOptions)?.compType === RCSlideOutPanelCompType.LINK_ROBOT_SELECTOR;
};


/**
 * Type guard check to make sure the provided options is the options for RCGatewayDefinitionPreviewCompOptions
 * @param options
 */
export const isGatewayDefinitionPreviewCompOptions = (options: unknown): options is RCGatewayDefinitionPreviewCompOptions => {
    return (options as SlideOutPanelOptions)?.compType === RCSlideOutPanelCompType.GATEWAY_DEFINITION_PREVIEW;
};


/**
 * Type guard check to make sure the provided options is the options for RCRobotDefinitionPreviewCompOptions
 * @param options
 */
export const isRobotDefinitionPreviewCompOptions = (options: unknown): options is RCRobotDefinitionPreviewCompOptions => {
    return (options as SlideOutPanelOptions)?.compType === RCSlideOutPanelCompType.ROBOT_DEFINITION_PREVIEW;
};


/**
 * Type guard check to make sure the provided options is the options for RCFileRequestPreviewCompOptions
 * @param options
 */
export const isFileRequestPreviewCompOptions = (options: unknown): options is RCFileRequestPreviewCompOptions => {
    return (options as SlideOutPanelOptions)?.compType === RCSlideOutPanelCompType.FILE_REQUEST_PREVIEW;
};



/**
 * Get the route path config for device details page. Because there are 2 paths that show device details
 *      - /projects/12/devices/78                       // without user/home context
 *      - /projects/12/users/34/home/56/devices/78      // with user/home context
 * The sub route settings for all of those paths are the same so instead of copy and paste this config, we will make a function that return
 * the config can call it when we need it. This way if we need to change the config, we only need to change 1 place
 *
 * @param rootPath - The path of the route.
 * @param hasHomeContext - Whether or not there is a home context. There is a sub route, data route, which is only available when the agent
 *                         is in a user's home.
 */
const getDeviceDetailsPageRouteGroup = (rootPath: string, projectUIConfig: ProjectUIConfig | undefined): RouteGroup => {

    // Create a subRoute for FileRequest feature if
    // 1. There is no projectUIConfig (This mean we don't know which project we are looking at yet so we will need to create this route)
    // 2. There is a projectUIConfig and the settings contain `DOWNLOAD_FROM_DEVICE` | `DOWNLOAD_FROM_CONTROL_SERVER` file request feature
    // 3. There is a projectUIConfig and the settings contain `UPLOAD_TO_DEVICE` | `UPLOAD_TO_CONTROL_SERVER` file request feature
    const fileRequestSubRoute = !projectUIConfig
        || projectUIConfig.enabledFeatures?.fileTransfer?.supportedActions[FileRequestAction.DOWNLOAD_FROM_DEVICE] !== undefined
        || projectUIConfig.enabledFeatures?.fileTransfer?.supportedActions[FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER] !== undefined
        || projectUIConfig.enabledFeatures?.fileTransfer?.supportedActions[FileRequestAction.UPLOAD_TO_DEVICE] !== undefined
        || projectUIConfig.enabledFeatures?.fileTransfer?.supportedActions[FileRequestAction.UPLOAD_TO_CONTROL_SERVER] !== undefined
        ? {
            name        : RC_SUB_ROUTE_NAME.FILE_REQUESTS,
            relativePath: '/file-requests',
            label       : 'label.file_request_other',
        } as SubRoute
        : undefined;

    return {
        rootPath,
        subRoutes: [
            {
                name        : RC_SUB_ROUTE_NAME.ROBOTS,
                relativePath: '',
                label       : 'label.robot_other',
            } as SubRoute,
            {
                name        : RC_SUB_ROUTE_NAME.ISSUES,
                relativePath: '/issues',
                label       : 'label.issue_other',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.DATA,
                relativePath: '/data',
                label       : 'label.data',
            } as SubRoute,
            ...(fileRequestSubRoute ? [fileRequestSubRoute] : []),      // insert the fileRequestSubRoute in here if it exist
            {
                name        : DEFAULT_SUB_ROUTE_NAME.LOGS,
                relativePath: '/logs',
                label       : 'label.logs',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.SETTINGS,
                relativePath: '/settings',
                label       : 'label.settings',
            } as SubRoute,
        ],
    };
};


/**
 * Get the route path config for device details page. Because there can be multiple paths that show home details therefore we will make a function
 * that return the config can call it when we need it. This way if we need to change the config, we only need to change 1 place
 *
 * @param rootPath - The path of the route.
 */
const getHomeDetailsPageRouteGroup = (rootPath: string, projectUIConfig: ProjectUIConfig | undefined): RouteGroup => {

    // Create a subRoute for TSDB and Video feature. Because the default is show the TSDB and Videos tab therefore, we will only
    // HIDE these 2 tabs if "projectUIConfig.enabledFeatures.tsdbTab" is SPECIFICALLY set to false or "projectUIConfig.enabledFeatures.videosTab"
    // is SPECIFICALLY set to false
    const tsdbSubRoute = projectUIConfig?.enabledFeatures?.tsdbTabVisible === false
        ? undefined                                             // No TSDB tab
        : {
            name        : DEFAULT_SUB_ROUTE_NAME.TSDB,
            relativePath: '/tsdb',
            label       : 'label.tsdb',
        } as SubRoute;

    const videosSubRoute = projectUIConfig?.enabledFeatures?.videosTabVisible === false
        ? undefined                                             // No Videos tab
        : {
            name        : DEFAULT_SUB_ROUTE_NAME.VIDEOS,
            relativePath: '/videos',
            label       : 'label.video_other',
        } as SubRoute;

    return {
        rootPath,
        subRoutes: [
            {
                name        : DEFAULT_SUB_ROUTE_NAME.DEFAULT,
                relativePath: '',
                label       : 'label.overview',
            } as SubRoute,
            {
                name        : RC_SUB_ROUTE_NAME.ROBOTS_STATUS,
                relativePath: '/robots-status',
                label       : 'label.robots_status',
            } as SubRoute,
            {
                name        : RC_SUB_ROUTE_NAME.ROBOTS,
                relativePath: '/robots',
                label       : 'label.robot_other',
            } as SubRoute,
            {
                name        : RC_SUB_ROUTE_NAME.ISSUES,
                relativePath: '/issues',
                label       : 'label.issue_other',
            } as SubRoute,
            ...(tsdbSubRoute ? [tsdbSubRoute] : []),      // insert the tsdbSubRoute in here if it exist
            ...(videosSubRoute ? [videosSubRoute] : []),      // insert the videosSubRoute in here if it exist
            {
                name        : DEFAULT_SUB_ROUTE_NAME.SETTINGS,
                relativePath: '/settings',
                label       : 'label.settings',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.LOGS,
                relativePath: '/logs',
                label       : 'label.logs',
            } as SubRoute,
        ],
    };
};



/**
 * Get the route path config for robot details page. Because there are 4 paths that show robot details
 *      - /projects/12/robots/abc                                   // without any context
 *      - /projects/12/devices/78/robots/abc                        // with only device context but no user/home context
 *      - /projects/12/users/34/homes/56/robots/abc                 // with user/home context only but no device context
 *      - /projects/12/users/34/homes/56/devices/78/robots/abc      // with user/home/device context
 * The sub route settings for all of those paths are the same so instead of copy and paste this config, we will make a function that return
 * the config can call it when we need it. This way if we need to change the config, we only need to change 1 place
 *
 * @param rootPath - The path of the route.
 */
const getRobotDetailsPageRouteGroup = (rootPath: string, projectUIConfig: ProjectUIConfig | undefined): RouteGroup => {

    // Create a subRoute for FileRequest feature if
    // 1. There is no projectUIConfig (This mean we don't know which project we are looking at yet so we will create this route)
    // 2. There is a projectUIConfig and the settings contain `DOWNLOAD_FROM_ROBOT` file request feature
    // 3. There is a projectUIConfig and the settings contain `UPLOAD_TO_ROBOT` file request feature
    const fileRequestSubRoute = !projectUIConfig
        || projectUIConfig.enabledFeatures?.fileTransfer?.supportedActions[FileRequestAction.DOWNLOAD_FROM_ROBOT] !== undefined
        || projectUIConfig.enabledFeatures?.fileTransfer?.supportedActions[FileRequestAction.UPLOAD_TO_ROBOT] !== undefined
        ? {
            name        : RC_SUB_ROUTE_NAME.FILE_REQUESTS,
            relativePath: '/file-requests',
            label       : 'label.file_request_other',
        } as SubRoute
        : undefined;

    return {
        rootPath,
        subRoutes: [
            {
                name        : DEFAULT_SUB_ROUTE_NAME.DASHBOARD,
                relativePath: '',
                label       : 'label.overview',
            } as SubRoute,
            {
                name        : RC_SUB_ROUTE_NAME.ISSUES,
                relativePath: '/issues',
                label       : 'label.issue_other',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.DATA,
                relativePath: '/data',
                label       : 'label.data',
            } as SubRoute,
            ...(fileRequestSubRoute ? [fileRequestSubRoute] : []),      // insert the fileRequestSubRoute in here if it exist
            {
                name        : DEFAULT_SUB_ROUTE_NAME.SETTINGS,
                relativePath: '/settings',
                label       : 'label.settings',
            } as SubRoute,
        ],
    };
};



/**
 * Get the Route settings for the Robot DASHBOARD page.
 * There are multiple Routes that show the same page. The route config is exactly the same except for the path.
 *      /projects/12/robots/abc
 *      /projects/12/users/34/homes/56/robots/abc
 *      /projects/12/users/34/homes/56/devices/78/robots/abc
 *      /projects/12/devices/78/robots/abc
 * So instead of copy and paste this block of <RitzProjectRoute /> in multiple places, we will call this function to create and return the HTML.
 * @param routeGroup
 */
const getRobotDashboardPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, DEFAULT_SUB_ROUTE_NAME.DASHBOARD)}
            element={(
                <RitzProjectRoute
                    element={<RCRobotDashboardPage />}
                />
            )}
        />
    );
};


/**
 * Get the Route settings for the Robot ISSUES page.
 * There are multiple Routes that show the same page. The route config is exactly the same except for the path.
 *      /projects/12/robots/abc/issues
 *      /projects/12/users/34/homes/56/robots/abc/issues
 *      /projects/12/users/34/homes/56/devices/78/robots/abc/issues
 *      /projects/12/devices/78/robots/abc/issues
 * So instead of copy and paste this block of <RitzProjectRoute /> in multiple places, we will call this function to create and return the HTML.
 * @param routeGroup
 */
const getRobotIssuesPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, RC_SUB_ROUTE_NAME.ISSUES)}
            element={(
                <RitzProjectRoute
                    element={<RCRobotIssuesPage />}
                />
            )}
        />
    );
};


/**
 * Get the Route settings for the Robot DATA page.
 * There are multiple Routes that show the same page. The route config is exactly the same except for the path.
 *      /projects/12/robots/abc/data
 *      /projects/12/users/34/homes/56/robots/abc/data
 *      /projects/12/users/34/homes/56/devices/78/robots/abc/data
 *      /projects/12/devices/78/robots/abc/data
 * So instead of copy and paste this block of <RitzProjectRoute /> in multiple places, we will call this function to create and return the HTML.
 * @param routeGroup
 */
const getRobotDataPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, DEFAULT_SUB_ROUTE_NAME.DATA)}
            element={(
                <RitzProjectRoute
                    element={<RCRobotHistoricalDataPage />}
                />
            )}
        />
    );
};


/**
 * Get the Route settings for the Robot FILE REQUESTS page.
 * There are multiple Routes that show the same page. The route config is exactly the same except for the path.
 *      /projects/12/robots/abc/file-requests
 *      /projects/12/users/34/homes/56/robots/abc/file-requests
 *      /projects/12/users/34/homes/56/devices/78/robots/abc/file-requests
 *      /projects/12/devices/78/robots/abc/file-requests
 * So instead of copy and paste this block of <RitzProjectRoute /> in multiple places, we will call this function to create and return the HTML.
 * @param routeGroup
 */
const getRobotFileRequestsPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, RC_SUB_ROUTE_NAME.FILE_REQUESTS)}
            element={(
                <RitzProjectRoute
                    element={<RCFileRequestsPage />}
                />
            )}
        />
    );
};


/**
 * Get the Route settings for the Robot SETTINGS page.
 * There are multiple Routes that show the same page. The route config is exactly the same except for the path.
 *      /projects/12/robots/abc/settings
 *      /projects/12/users/34/homes/56/robots/abc/settings
 *      /projects/12/users/34/homes/56/devices/78/robots/abc/settings
 *      /projects/12/devices/78/robots/abc/settings
 * So instead of copy and paste this block of <RitzProjectRoute /> in multiple places, we will call this function to create and return the HTML.
 * @param routeGroup
 */
const getRobotSettingPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, DEFAULT_SUB_ROUTE_NAME.SETTINGS)}
            element={(
                <RitzProjectRoute
                    element={<RCRobotInfoPage />}
                />
            )}
        />
    );
};


const getHomeDefaultPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, DEFAULT_SUB_ROUTE_NAME.DEFAULT)}
            element={(
                <RitzProjectRoute
                    element={<RCHomeDetailsPage />}
                />
            )}
        />
    );
};

const getHomeRobotsPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, RC_SUB_ROUTE_NAME.ROBOTS)}
            element={(
                <RitzProjectRoute
                    element={<RCHomeRobotsPage />}
                />
            )}
        />
    );
};

const getHomeIssuesPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, RC_SUB_ROUTE_NAME.ISSUES)}
            element={(
                <RitzProjectRoute
                    element={<RCHomeIssuesPage />}
                />
            )}
        />
    );
};

const getHomeRobotStatusPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, RC_SUB_ROUTE_NAME.ROBOTS_STATUS)}
            element={(
                <RitzProjectRoute
                    element={<RCHomeRobotsStatusPage />}
                />
            )}
        />
    );
};

const getDeviceRobotsPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, RC_SUB_ROUTE_NAME.ROBOTS)}
            element={(
                <RitzProjectRoute
                    element={<RCDeviceRobotsPage />}
                />
            )}
        />
    );
};

const getDeviceIssuesPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, RC_SUB_ROUTE_NAME.ISSUES)}
            element={(
                <RitzProjectRoute
                    element={<RCDeviceIssuesPage />}
                />
            )}
        />
    );
};

const getDeviceSettingsPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, DEFAULT_SUB_ROUTE_NAME.SETTINGS)}
            element={(
                <RitzProjectRoute
                    element={<RCDeviceSettingsPage />}
                />
            )}
        />
    );
};

const getDeviceDataPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, DEFAULT_SUB_ROUTE_NAME.DATA)}
            element={(
                <RitzProjectRoute
                    element={<RCDeviceCollectionsLatestValuesDisplayPage />}
                />
            )}
        />
    );
};

const getDeviceFileRequestsPageRouteComponent = (routeGroup: RouteGroup): JSX.Element => {
    return (
        <Route
            path={getSubRouteAbsolutePath(routeGroup, RC_SUB_ROUTE_NAME.FILE_REQUESTS)}
            element={(
                <RitzProjectRoute
                    element={<RCFileRequestsPage />}
                />
            )}
        />
    );
};


const getInvestigateIssuePageRouteComponent = (routePath: string): JSX.Element => {
    return (
        <Route
            path={routePath}
            element={(
                <RitzProjectRoute
                    element={<RCInvestigateIssuePage />}
                />
            )}
        />
    );
};


const getGenericDataViewPageRouteComponent = (routeName: string): JSX.Element => {
    return (
        <Route
            path={routeName}
            element={(
                <RitzProjectRoute
                    element={<GenericDataViewPage />}
                />
            )}
        />
    );
};


export enum ADMIN_PAGE_SUB_ROUTE_NAME {
    GATEWAY_CATALOG = 'gateway_catalog',
    ROBOT_CATALOG = 'robot_catalog'
}



/**
 * Create and return a complete map of all the paths a RC project type has.
 */
const getRCRoutePaths = (project: Project | undefined, loggedInAgent: Agent | undefined) => {

    const projectUIConfig = ProjectTypeHandlersManager.getInstance().getProjectCustomUIHandler(
        project?.supportToolSettings.projectType,
    )?.getCustomUIConfig(loggedInAgent, project);

    return {
        // Route paths for Investigate issue page
        PROJECTS_$projectId_ISSUES_$issueId                                              : '/projects/:projectId/issues/:issueId',
        PROJECTS_$projectId_HOMES_$homeId_ISSUES_$issueId                                : '/projects/:projectId/homes/:homeId/issues/:issueId',
        PROJECTS_$projectId_DEVICES_$deviceId_ISSUES_$issueId                            : '/projects/:projectId/devices/:deviceId/issues/:issueId',
        PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId_ISSUES_$issueId              : '/projects/:projectId/homes/:homeId/devices/:deviceId/issues/:issueId',
        PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId_ISSUES_$issueId: '/projects/:projectId/users/:userId/homes/:homeId/devices/:deviceId/issues/:issueId',
        // Route paths for the generic historical data view page
        PROJECTS_$projectId_DEVICES_$deviceId_DATA_$dataId                               : '/projects/:projectId/devices/:deviceId/data/:dataId',
        PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId_DATA_$dataId                 : '/projects/:projectId/homes/:homeId/devices/:deviceId/data/:dataId',
        PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId_DATA_$dataId   : '/projects/:projectId/users/:userId/homes/:homeId/devices/:deviceId/data/:dataId',
        // Route paths for Home landing page
        PROJECTS_$projectId_HOMES_$homeId                                                : getHomeDetailsPageRouteGroup('/projects/:projectId/homes/:homeId', projectUIConfig),
        PROJECTS_$projectId_USERS_$userId_HOMES_$homeId                                  : getHomeDetailsPageRouteGroup('/projects/:projectId/users/:userId/homes/:homeId', projectUIConfig),
        // Route paths for Device landing page
        PROJECTS_$projectId_DEVICES_$deviceId                                            : getDeviceDetailsPageRouteGroup('/projects/:projectId/devices/:deviceId', projectUIConfig),
        PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId                              : getDeviceDetailsPageRouteGroup('/projects/:projectId/homes/:homeId/devices/:deviceId', projectUIConfig),
        PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId                : getDeviceDetailsPageRouteGroup('/projects/:projectId/users/:userId/homes/:homeId/devices/:deviceId', projectUIConfig),
        // Route paths for Robot landing page
        PROJECTS_$projectId_ROBOTS_$robotId                                              : getRobotDetailsPageRouteGroup('/projects/:projectId/robots/:robotId', projectUIConfig),
        PROJECTS_$projectId_HOMES_$homeId_ROBOTS_$robotId                                : getRobotDetailsPageRouteGroup('/projects/:projectId/homes/:homeId/robots/:robotId', projectUIConfig),
        PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId_ROBOTS_$robotId              : getRobotDetailsPageRouteGroup('/projects/:projectId/homes/:homeId/devices/:deviceId/robots/:robotId', projectUIConfig),
        PROJECTS_$projectId_DEVICES_$deviceId_ROBOTS_$robotId                            : getRobotDetailsPageRouteGroup('/projects/:projectId/devices/:deviceId/robots/:robotId', projectUIConfig),
        PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_ROBOTS_$robotId                  : getRobotDetailsPageRouteGroup('/projects/:projectId/users/:userId/homes/:homeId/robots/:robotId', projectUIConfig),
        PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId_ROBOTS_$robotId: getRobotDetailsPageRouteGroup('/projects/:projectId/users/:userId/homes/:homeId/devices/:deviceId/robots/:robotId', projectUIConfig),
        // Route paths for other pages
        PROJECTS_$projectId_MONITORING                                                   : '/projects/:projectId/monitoring',
        PROJECTS_$projectId_ISSUES                                                       : '/projects/:projectId/issues',
        PROJECTS_$projectId_ROBOTS                                                       : '/projects/:projectId/robots',
        PROJECTS_$projectId_RC_ADMIN_GATEWAY_CATALOG                                     : '/projects/:projectId/admin/gatewayCatalog',
        PROJECTS_$projectId_RC_ADMIN_ROBOT_CATALOG                                       : '/projects/:projectId/admin/robotCatalog',
    };
};


/**
 * Define the path for RobotCloud project in additional to the default Ritz route paths. This is NOT the complete list of routes for the
 * entire project. This is just the list of routes that will be added for RobotCloud project type.
 */
export const RC_ROUTE_PATHS = getRCRoutePaths(undefined, undefined);



/**
 * Create a class to handle RobotCloud project type
 */
class RobotCloudProjectTypeHandler implements
    ProjectTypeSlideOutCompHandler,
    ProjectTypeAppProxyHandler,
    ProjectTypeRoutesHandler,
    ProjectTypeCustomUIHandler,
    ProjectTypePageLayoutHandler {


    private cachedRCRoutePathsByProjectId = new Map<number, {}>();


    /**
     * Implement the function from ProjectTypeAppProxyHandler interface
     */
    public getAppProxyAPI (): AppProxyAPI {
        return RobotCloudAPI.getInstance();
    }


    /**
     * Implement the function from ProjectTypePageLayoutHandler interface
     * @returns
     */
    public getPageInfoComponent (): React.FC<RCPageInfoBarContainerProps> {
        return RCPageInfoBarContainer;
    }


    /**
     * Fetch Gate Definitions for the whole project
     * @param projectType
     */
    public async getGatewayCatalog (projectId: number): Promise<readonly GatewayDefinition[]> {
        return RobotCloudAPI.getInstance().getGatewayCatalog(projectId);
    }


    /**
     * The Route Paths for RobotCloud will be consists of the DEFAULT_ROUTE_PATHS merged with RC_ROUTE_PATHS
     */
    public getRoutePaths (project: Project | undefined, loggedInAgent: Agent | undefined) {

        // If we have project and projectUISettings, see if we have a customized route paths for the selected project
        if (project) {

            // Find the routePaths for the selected project.id and return it if it exists
            const cachedRCRoutePaths = this.cachedRCRoutePathsByProjectId.get(project.id);
            if (cachedRCRoutePaths) {
                return cachedRCRoutePaths;
            }

            // If custom routePaths for the selected project.id does not exist in cache, create one
            const customRCRoutePaths = getRCRoutePaths(project, loggedInAgent);

            // Combine RITZ's default route path and the project's custom route paths
            const combinedRoutePaths = {
                ...DEFAULT_ROUTE_PATHS,
                ...customRCRoutePaths,
            };

            // Now cache the new custom route paths so that we don't create another one the next time this function is called
            this.cachedRCRoutePathsByProjectId.set(project.id, combinedRoutePaths);

            return combinedRoutePaths;
        }

        // If there is no project selected, return the default route paths that we use for all RC projects.
        return {
            ...DEFAULT_ROUTE_PATHS,
            ...RC_ROUTE_PATHS,
        };
    }


    /**
     * This defines what component to use for which route PATH for the routes that are project related.
     * We only need to define the components for the RC custom routes AND/OR default Ritz routes that we want to override the component.
     */
    public getRoutesComponent (project: Project | undefined, loggedInAgent: Agent | undefined): readonly JSX.Element[] {
        return [
            <Route
                path={DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_DEVICES}          // Override the Default ProjectDevicesPage
                element={(
                    <RitzProjectRoute
                        element={<RCProjectDevicesPage />}
                    />
                )}
            />,
            <Route
                path={getSubRouteAbsolutePath(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_ADMIN as RouteGroup, DEFAULT_SUB_ROUTE_NAME.DEFAULT)}          // Override the Default Project Settings page
                element={(
                    <RitzProjectRoute
                        element={<RCProjectSettingsPage />}
                    />
                )}
            />,
            <Route
                path={DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_DEVICES}          // Override the Default DeviceSettingsPage
                element={(
                    <RitzProjectRoute
                        element={<RCProjectDevicesPage />}
                    />
                )}
            />,
            <Route
                path={RC_ROUTE_PATHS.PROJECTS_$projectId_MONITORING}
                element={(
                    <RitzProjectRoute
                        element={<RCMonitorsPage />}
                    />
                )}
            />,
            <Route
                path={RC_ROUTE_PATHS.PROJECTS_$projectId_ISSUES}
                element={(
                    <RitzProjectRoute
                        element={<RCIssuesPage />}
                    />
                )}
            />,
            <Route
                path={RC_ROUTE_PATHS.PROJECTS_$projectId_ROBOTS}
                element={(
                    <RitzProjectRoute
                        element={<RCRobotsPage />}
                    />
                )}
            />,

            // Device's Robots page. For example: /projects/12/devices/34/robots
            getDeviceRobotsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId as RouteGroup),
            getDeviceRobotsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup),
            getDeviceRobotsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup),

            // Device's Issues page. For example: /projects/12/devices/34/issues
            getDeviceIssuesPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId as RouteGroup),
            getDeviceIssuesPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup),
            getDeviceIssuesPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup),

            // Device's Settings page. For example: /projects/12/devices/34/settings
            getDeviceSettingsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId as RouteGroup),
            getDeviceSettingsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup),
            getDeviceSettingsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup),

            // Device's Data page. For example: /projects/12/devices/34/data
            getDeviceDataPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId as RouteGroup),
            getDeviceDataPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup),
            getDeviceDataPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup),

            // Device's File Requests page. For example: /projects/12/devices/34/file-requests
            getDeviceFileRequestsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId as RouteGroup),
            getDeviceFileRequestsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup),
            getDeviceFileRequestsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup),

            // Route component for investigate issue pages. For example: /projects/1310/issues/1269
            getInvestigateIssuePageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_ISSUES_$issueId),
            getInvestigateIssuePageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId_ISSUES_$issueId),
            getInvestigateIssuePageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_ISSUES_$issueId),
            getInvestigateIssuePageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId_ISSUES_$issueId),
            getInvestigateIssuePageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId_ISSUES_$issueId),


            // Route component for Home's landing page. For example: /projects/1310/homes/1234
            getHomeDefaultPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId as RouteGroup),
            getHomeDefaultPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId as RouteGroup),

            // Route component for Home's Robots Status page. For example: /projects/1310/homes/1234/robots-status
            getHomeRobotStatusPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId as RouteGroup),
            getHomeRobotStatusPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId as RouteGroup),

            // Route component for Home's Robots. For example: /projects/1310/homes/1234/robots
            getHomeRobotsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId as RouteGroup),
            getHomeRobotsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId as RouteGroup),

            // Route component for Home's Issues page. For example: /projects/1310/homes/1234/issues
            getHomeIssuesPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId as RouteGroup),
            getHomeIssuesPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId as RouteGroup),

            // Route component for Robot's landing page. For example: /projects/1310/robots/robot_1
            getRobotDashboardPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_ROBOTS_$robotId as RouteGroup),
            getRobotDashboardPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup),
            getRobotDashboardPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_ROBOTS_$robotId as RouteGroup),
            getRobotDashboardPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup),
            getRobotDashboardPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_ROBOTS_$robotId as RouteGroup),
            getRobotDashboardPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup),

            // Route component for Robot's Issues page. For example: /projects/1310/robots/robot_1/issues
            getRobotIssuesPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_ROBOTS_$robotId as RouteGroup),
            getRobotIssuesPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup),
            getRobotIssuesPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_ROBOTS_$robotId as RouteGroup),
            getRobotIssuesPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup),
            getRobotIssuesPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_ROBOTS_$robotId as RouteGroup),
            getRobotIssuesPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup),

            // Route component for Robot's Data page. For example: /projects/1310/robots/robot_1/data
            getRobotDataPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_ROBOTS_$robotId as RouteGroup),
            getRobotDataPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup),
            getRobotDataPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_ROBOTS_$robotId as RouteGroup),
            getRobotDataPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup),
            getRobotDataPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_ROBOTS_$robotId as RouteGroup),
            getRobotDataPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup),

            // Route component for Robot's File Requests page. For example: /projects/1310/robots/file-requests
            getRobotFileRequestsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_ROBOTS_$robotId as RouteGroup),
            getRobotFileRequestsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup),
            getRobotFileRequestsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_ROBOTS_$robotId as RouteGroup),
            getRobotFileRequestsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup),
            getRobotFileRequestsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_ROBOTS_$robotId as RouteGroup),
            getRobotFileRequestsPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup),

            // Route component for Robot's Settings page. For example: /projects/1310/robots/settings
            getRobotSettingPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_ROBOTS_$robotId as RouteGroup),
            getRobotSettingPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup),
            getRobotSettingPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_ROBOTS_$robotId as RouteGroup),
            getRobotSettingPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup),
            getRobotSettingPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_ROBOTS_$robotId as RouteGroup),
            getRobotSettingPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup),

            // Route component for generic Historical Data view page. For example: /projects/1310/devices/1234/data/data_id
            getGenericDataViewPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId_DATA_$dataId),
            getGenericDataViewPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId_DATA_$dataId),
            getGenericDataViewPageRouteComponent(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId_DATA_$dataId),

            <Route
                path={RC_ROUTE_PATHS.PROJECTS_$projectId_RC_ADMIN_GATEWAY_CATALOG as string}
                element={(
                    <RitzProjectRoute
                        element={<RCGatewayCatalogPage />}
                    />
                )}
            />,

            <Route
                path={RC_ROUTE_PATHS.PROJECTS_$projectId_RC_ADMIN_ROBOT_CATALOG as string}
                element={(
                    <RitzProjectRoute
                        element={<RCRobotCatalogPage />}
                    />
                )}
            />,
        ];
    }



    public getSlideOutComponent (options: SlideOutPanelOptions): React.ReactElement<SlideOutCompProps> | undefined {
        if (isMonitorPreviewCompOptions(options)) {
            return (
                <RCMonitorPreviewContainer
                    projectId={options.compProps.data.projectId}
                    monitorId={options.compProps.data.monitorId}
                    preSelectedRobotClass={options.compProps.data.preSelectedRobotClass}
                    preSelectedRobotId={options.compProps.data.preSelectedRobotId}
                    preSelectedHomeId={options.compProps.data.preSelectedHomeId}
                />
            );
        }

        if (isIssuePreviewCompOptions(options)) {
            return (
                <RCIssuePreviewContainer
                    projectId={options.compProps.data.projectId}
                    issueId={options.compProps.data.issueId}
                    preSelectedRobotId={options.compProps.data.preSelectedRobotId}
                    preSelectedHomeId={options.compProps.data.preSelectedHomeId}
                    context={options.compProps.context}
                />
            );
        }

        if (isRobotPreviewCompOptions(options)) {
            return (
                <RCRobotPreviewContainer
                    projectId={options.compProps.data.projectId}
                    homeId={options.compProps.data.homeId}
                    robotId={options.compProps.data.robotId}
                    context={options.compProps.context}
                />
            );
        }


        if (isAlertPreviewCompOptions(options)) {
            return (
                <RCAlertPreviewContainer
                    projectId={options.compProps.data.projectId}
                    alertId={options.compProps.data.alertId}
                    homeId={options.compProps.data.homeId}
                    robotId={options.compProps.data.robotId}
                    context={options.compProps.context}
                />
            );
        }

        if (isLinkRobotSelectorCompOptions(options)) {
            return (
                <RCLinkRobotPreviewContainer
                    projectId={options.compProps.data.projectId}
                    homeId={options.compProps.data.homeId}
                    deviceId={options.compProps.data.deviceId}
                />
            );
        }

        if (isGatewayDefinitionPreviewCompOptions(options)) {
            return (
                <RCGatewayDefinitionPreviewContainer
                    projectId={options.compProps.data.projectId}
                    deviceClass={options.compProps.data.deviceClass}
                />
            );
        }

        if (isRobotDefinitionPreviewCompOptions(options)) {
            return (
                <RCRobotDefinitionPreviewContainer
                    projectId={options.compProps.data.projectId}
                    robotClass={options.compProps.data.robotClass}
                />
            );
        }

        if (isFileRequestPreviewCompOptions(options)) {
            return (
                <RCFileRequestPreviewContainer
                    projectId={options.compProps.data.projectId}
                    homeId={options.compProps.data.homeId}
                    deviceId={options.compProps.data.deviceId}
                    robotId={options.compProps.data.robotId}
                    selectedFileRequest={options.compProps.data.selectedFileRequest}
                    context={options.compProps.context}
                />
            );
        }

        return undefined;
    }


    /**
     * Implement the ProjectTypeCustomUIHandler interface. Any project type that want to have custom UI needs to implement this
     * interface and implement these function.
     * @param loggedInAgent
     * @param project
     */
    public getCustomUIConfig (loggedInAgent: Agent | undefined, project: Project | undefined) {
        return integrateProjectTypeCustomUIConfigWithProjectCustomConfig(loggedInAgent, project, getRCDefaultProjectUIConfig(loggedInAgent, project));
    }
}
const rcProjectHandler = new RobotCloudProjectTypeHandler();


/**
 * Initialize the RobotCloud project handler by register the RobotCloudProjectTypeHandler with the ProjectTypeHandlersManager. Without this,
 * everything will still work but RobotCloud project will not have the RobotCloud specific features and the agent can only access the default
 * Ritz features.
 */
export const initializeRobotCloudProjectType = () => {
    const handlersManager = ProjectTypeHandlersManager.getInstance();

    handlersManager.registerAppProxyHandler(ProjectType.ROBOT_CLOUD, rcProjectHandler);
    handlersManager.registerRoutesHandler(ProjectType.ROBOT_CLOUD, rcProjectHandler);
    handlersManager.registerProjectCustomUIHandler(ProjectType.ROBOT_CLOUD, rcProjectHandler);
    handlersManager.registerPageLayoutHandler(ProjectType.ROBOT_CLOUD, rcProjectHandler);

    // register the handler to handle the RobotCloud components that can be displayed in the Slide Out panel
    handlersManager.registerSlideOutComponentHandler(RCSlideOutPanelCompType.MONITOR_PREVIEW, rcProjectHandler);
    handlersManager.registerSlideOutComponentHandler(RCSlideOutPanelCompType.ALERT_PREVIEW, rcProjectHandler);
    handlersManager.registerSlideOutComponentHandler(RCSlideOutPanelCompType.ISSUE_PREVIEW, rcProjectHandler);
    handlersManager.registerSlideOutComponentHandler(RCSlideOutPanelCompType.ROBOT_PREVIEW, rcProjectHandler);
    handlersManager.registerSlideOutComponentHandler(RCSlideOutPanelCompType.LINK_ROBOT_SELECTOR, rcProjectHandler);
    handlersManager.registerSlideOutComponentHandler(RCSlideOutPanelCompType.GATEWAY_DEFINITION_PREVIEW, rcProjectHandler);
    handlersManager.registerSlideOutComponentHandler(RCSlideOutPanelCompType.ROBOT_DEFINITION_PREVIEW, rcProjectHandler);
    handlersManager.registerSlideOutComponentHandler(RCSlideOutPanelCompType.FILE_REQUEST_PREVIEW, rcProjectHandler);
};
